import { getNullableMoment } from '@intelligence/v3/utils/date';
import { formatKMB } from '@intelligence/v3/utils/number';

/**
 * 当日小时级数据点位数量（00:00 - 23:00 + 23:59）
 * 后端返回 25 个点，对应 24 小时 + 末端 23:59
 */
export const FULL_DAY_HOUR_COUNT = 25;

/** X 轴槽位数量（与数据点位一致） */
export const X_AXIS_SLOT_COUNT = FULL_DAY_HOUR_COUNT;

/** X 轴末端时间（23:59） */
export const X_AXIS_END_VALUE = '23:59';

/**
 * 计算日期范围（今日 / 昨日 / 上周同日）
 */
export const calculateDateRange = (baseDate: string) => {
  const dateM = getNullableMoment(baseDate);
  if (!dateM) return { today: baseDate, yesterday: '', lastWeek: '' };
  return {
    today: baseDate,
    yesterday: dateM.clone().add(-1, 'days').format('YYYY-MM-DD'),
    lastWeek: dateM.clone().add(-7, 'days').format('YYYY-MM-DD'),
  };
};

/**
 * 构建 X 轴类目（00:00 - 23:00 + 23:59）
 * 与后端返回的 25 个点一一对应
 */
export const buildFullDayXAxisData = (): string[] => [
  ...Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`),
  X_AXIS_END_VALUE,
];

/**
 * 根据容器宽度计算 X 轴刻度间隔（2h / 4h / 6h）
 */
export const getTickIntervalHours = (width: number): number => {
  if (width >= 1440) return 2;
  if (width >= 1024) return 4;
  return 6;
};

/**
 * 判断小时索引是否应显示 X 轴刻度
 * - 00:00 不显示
 * - 末端 23:59 始终显示
 * - 其余按 interval 间隔均匀分布
 */
export const shouldShowXAxisTick = (
  hourIndex: number,
  totalPoints: number,
  tickIntervalHours: number,
): boolean => {
  if (hourIndex === totalPoints - 1) return true;
  return (hourIndex + 1) % tickIntervalHours === 0;
};

/**
 * 格式化 X 轴刻度标签
 * - 末端 23:59 直接返回
 * - 其余返回原始值（如 00:00, 02:00, ...）
 */
export const formatHourlyDisplayTime = (
  axisValue: string,
  dataIndex?: number,
  totalSlots: number = X_AXIS_SLOT_COUNT,
): string => {
  if (dataIndex === totalSlots - 1) {
    return X_AXIS_END_VALUE;
  }
  return axisValue;
};

/**
 * 从小时级数组提取 value 序列（直接映射 25 个点）
 * 后端返回 25 个点（00:00 - 23:00 + 23:59），直接提取即可
 */
export const extractHourlyValues = (
  points?: Array<{ hour: string; value: number | null }>,
): Array<number | null> => {
  const pointsArray = points || [];
  return Array.from({ length: FULL_DAY_HOUR_COUNT }, (_, i) =>
    pointsArray[i]?.value ?? null,
  );
};

/**
 * 格式化 Y 轴标签（0 特殊处理）
 */
export const formatYAxisLabel = (value: number): string => {
  if (value === 0) return '0';
  return formatKMB(value);
};

/**
 * 获取数组中最后一个有效数值的索引
 */
export const findLastValidValueIndex = (
  values?: Array<number | null>,
): number => {
  if (!values?.length) return -1;
  let index = values.length - 1;
  while (index >= 0) {
    if (values[index] !== null && values[index] !== undefined) return index;
    index -= 1;
  }
  return -1;
};
